<template>
	<PopoverGroup class="hidden md:flex space-x-7 items-center">
		<Popover
			v-for="navigationElement in menu"
			:key="navigationElement.id"
			v-slot="{ open, close }"
			class="!mx-0"
		>
			<PopoverButton
				:id="`CategoryId-${navigationElement.id}`"
				class="flex items-center text-[15px] lg:leading-none text-current outline-none"
				:class="
					headerMode === 'transparent'
						? 'hover:border-white'
						: 'hover:border-secondary'
				"
				@mouseover="(e) => hoverPopover(e, open)"
				@mouseleave="closePopover(close)"
			>
				<div class="flex items-center">
					<NuxtLink
						:to="hrefOf(navigationElement)"
						class="nav-link py-5 px-2.5 hover:underline"
						:class="
							isActiveRoute(navigationElement).value
								? 'router-link-exact-active nav-link-dropdown'
								: ''
						"
						@click.stop="handleNavigate(close)"
					>
						{{ navigationElement.name }}
					</NuxtLink>
					<template v-if="navigationElement.children.length">
						<ChevronDownIcon
							v-if="!open"
							class="h-5 w-5 flex-none text-current relative -left-2"
							aria-hidden="true"
						/>
						<ChevronUpIcon
							v-if="open"
							class="h-5 w-5 flex-none text-current relative -left-2"
							aria-hidden="true"
						/>
					</template>
				</div>
			</PopoverButton>

			<transition
				v-if="navigationElement.children.length"
				:css="false"
				@enter="onEnter"
				@leave="onLeave"
			>
				<PopoverPanel
					v-show="open"
					:id="`navigation-panel-${navigationElement.id}`"
					class="dropdown absolute bottom-0 left-0 translate translate-y-full w-full z-50 overflow-hidden bg-white shadow-lg border border-gray"
					static
					@mouseover.prevent="popoverHover = true"
					@mouseleave.prevent="closePopover(close)"
				>
					<div
						class="container mx-auto flex justify-between gap-8 pt-16 pb-28 px-12"
					>
						<div
							v-if="navigationElement.children.length"
							class="columns-4 columns-gray-dark w-full"
						>
							<div
								v-for="childElement in navigationElement.children"
								:key="childElement.id"
								class="group mb-8 px-4 min-w-[218px] break-inside-avoid relative flex leading-6"
							>
								<div class="flex-auto block font-bold">
									<NuxtLink
										:to="hrefOf(childElement)"
										class="text-black hover:underline"
										@click="handleNavigate(close)"
										>{{ childElement.name }}
									</NuxtLink>
									<ul class="flex flex-col">
										<template
											v-for="subChildElement in childElement.children"
											:key="subChildElement.id"
										>
											<div class="flex flex-col flex-grow">
												<p class="py-[3px] text-sm font-normal">
													<NuxtLink
														:to="hrefOf(subChildElement)"
														class="text-gray-super-dark hover:text-black hover:underline"
														@click="handleNavigate(close)"
													>
														{{ subChildElement.name }}
													</NuxtLink>
												</p>
											</div>
										</template>
									</ul>
								</div>
							</div>
						</div>
					</div>
				</PopoverPanel>
			</transition>
		</Popover>
	</PopoverGroup>
</template>

<script setup lang="ts">
import {
	Popover,
	PopoverButton,
	PopoverGroup,
	PopoverPanel
} from "@headlessui/vue";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/vue/20/solid";
import { computed, onBeforeUnmount, onMounted } from "vue";
import type { ResolvedMenuNode } from "~/types/storyblok/storyblok";

interface Props {
	headerMode: string;
	// The Menu Node menu, or the old Norce categories in the same shape
	// (useMenuWithFallback in LayoutHeaderDesktop).
	menu: ResolvedMenuNode[];
}

const { headerMode, menu } = defineProps<Props>();

const { hrefOf } = useMenuWithFallback(() => ({ menu }));

const { kebabCase } = useNorcePwa();

const route = useRoute();

const { onEnter, onLeave } = useHeightTransition({
	enterMs: 300,
	leaveMs: 260
});

const popoverHover = ref(false);
const popoverTimeout = ref();

/**
 * Closing the panel re-lays out the page under a stationary cursor, so the
 * browser fires a fresh `mouseover` that would re-open it. Cleared by real
 * pointer movement.
 */
const suppressHoverOpen = ref(false);
const clearHoverSuppression = (): void => {
	suppressHoverOpen.value = false;
};

onMounted(() =>
	window.addEventListener("mousemove", clearHoverSuppression, {
		passive: true
	})
);
onBeforeUnmount(() =>
	window.removeEventListener("mousemove", clearHoverSuppression)
);

/**
 * A link inside the menu was followed: close the panel and keep it closed.
 * The top-level link also stops the click from reaching its `PopoverButton`,
 * whose toggle would otherwise undo the close.
 */
const handleNavigate = (close: () => void): void => {
	suppressHoverOpen.value = true;
	popoverHover.value = false;
	if (popoverTimeout.value) clearTimeout(popoverTimeout.value);
	close();
};

const hoverPopover = (e, open): void => {
	if (suppressHoverOpen.value) return;

	popoverHover.value = true;

	if (!open) {
		e.target.parentNode.click();
	}
};
const closePopover = (close): void => {
	popoverHover.value = false;

	if (popoverTimeout.value) clearTimeout(popoverTimeout.value);
	popoverTimeout.value = setTimeout(() => {
		if (!popoverHover.value) {
			close();
		}
	}, 100);
};

// The current path contains this item's `/category/<name>` slug. An item
// without a name (a Menu Node link left unnamed) never matches, or it would on
// every category page.
const pathHasName = (name: string): boolean =>
	!!name && route.path.includes(`/category/${kebabCase(name)}`);

// Function to check if the route is active for the given navigation element or its children
const isActiveRoute = (navigationElement: ResolvedMenuNode) =>
	computed(() => {
		// Check if the current route matches the parent element
		if (pathHasName(navigationElement.name)) {
			return true;
		}

		// Check if the current route matches any child or subchild element
		return navigationElement.children.some((childElement) => {
			if (pathHasName(childElement.name)) {
				return true;
			}

			return childElement.children.some((subChildElement) =>
				pathHasName(subChildElement.name)
			);
		});
	});
</script>
